import React, {useEffect, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {SelectButton} from "primereact/selectbutton";
import {Button} from "primereact/button";

import {DynamicPricingAuditService} from "../../service/DynamicPricingAuditService";
import DynamicPricingAuditTable from "./DynamicPricingAuditTable";
import {getDateString} from "../../utils/TimeUtil";

const auditService = new DynamicPricingAuditService();
const PAGE_SIZE = 20;

const DynamicPricingAuditDialog = ({t, campaignId, visible, onHide}) => {
    // "This campaign" drops to account-wide when false, which is the only way to
    // see rule changes that are not linked to any campaign's products yet.
    const [campaignOnly, setCampaignOnly] = useState(true);
    const [scope, setScope] = useState(null);
    const [actorEmail, setActorEmail] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [first, setFirst] = useState(0);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [actors, setActors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(null);

    const activeCampaignId = campaignOnly ? campaignId : undefined;

    useEffect(() => {
        if (!visible) return;
        setLoading(true);
        auditService.list({
            campaign_id: activeCampaignId,
            scope: scope || undefined,
            actor_email: actorEmail || undefined,
            // PrimeReact's Calendar yields LOCAL midnight for the picked day, so
            // the filter must be built from local date parts (getDateString), not
            // toISOString, which shifts to UTC and can land on the day before for
            // any non-UTC user.
            start_date: dateRange && dateRange[0] ? getDateString(dateRange[0]) : undefined,
            end_date: dateRange && dateRange[1] ? getDateString(dateRange[1]) : undefined,
            limit: PAGE_SIZE,
            offset: first,
        }).then(data => { setRows(data?.rows || []); setTotal(data?.total || 0); })
          .catch(() => { setRows([]); setTotal(0); })
          .finally(() => setLoading(false));
    }, [visible, activeCampaignId, scope, actorEmail, dateRange, first]);

    useEffect(() => {
        if (!visible) return;
        auditService.actors(activeCampaignId).then(data => setActors(data || [])).catch(() => setActors([]));
    }, [visible, activeCampaignId]);

    const resetPaging = (apply) => { setFirst(0); apply(); };

    const scopeOptions = [
        {label: t('dynamic_pricing_audit.filter_all'), value: null},
        {label: t('dynamic_pricing_audit.scope.template'), value: 'template'},
        {label: t('dynamic_pricing_audit.scope.campaign'), value: 'campaign'},
        {label: t('dynamic_pricing_audit.scope.product'), value: 'product'},
        {label: t('dynamic_pricing_audit.scope.account'), value: 'account'},
    ];
    const actorOptions = [{label: t('dynamic_pricing_audit.filter_all'), value: null}]
        .concat(actors.map(a => ({label: a.actor_name || a.actor_email, value: a.actor_email})));
    const rangeOptions = [
        {label: t('dynamic_pricing_audit.this_campaign'), value: true},
        {label: t('dynamic_pricing_audit.account_wide'), value: false},
    ];

    return (
        <Dialog header={t('dynamic_pricing_audit.title')} visible={visible} modal
                style={{width: '980px', maxWidth: '95vw'}} onHide={onHide}>
            <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12}}>
                <SelectButton value={campaignOnly} options={rangeOptions}
                              onChange={(e) => e.value !== null && resetPaging(() => setCampaignOnly(e.value))}/>
                <Dropdown value={scope} options={scopeOptions} style={{width: '12em'}}
                          placeholder={t('dynamic_pricing_audit.scope_filter')}
                          onChange={(e) => resetPaging(() => setScope(e.value))}/>
                <Dropdown value={actorEmail} options={actorOptions} style={{width: '16em'}}
                          placeholder={t('dynamic_pricing_audit.actor_filter')}
                          onChange={(e) => resetPaging(() => setActorEmail(e.value))}/>
                <Calendar value={dateRange} selectionMode="range" readOnlyInput showButtonBar
                          placeholder={t('dynamic_pricing_audit.date_filter')} style={{width: '16em'}}
                          onChange={(e) => resetPaging(() => setDateRange(e.value))}/>
                <Button icon="pi pi-filter-slash" className="p-button-text"
                        tooltip={t('dynamic_pricing_audit.clear_filters')}
                        onClick={() => resetPaging(() => {
                            setScope(null); setActorEmail(null); setDateRange(null);
                        })}/>
            </div>

            <DynamicPricingAuditTable t={t} rows={rows} loading={loading}
                                      first={first} pageSize={PAGE_SIZE} total={total}
                                      onPage={(e) => setFirst(e.first)}
                                      expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                                      emptyMessage={t('dynamic_pricing_audit.empty')}/>
        </Dialog>
    );
};

export default DynamicPricingAuditDialog;
